import * as React from "react"
import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-[var(--scaena-radius-control)] border border-transparent bg-clip-padding font-ui text-sm font-[550] whitespace-nowrap outline-none select-none transition-[transform,box-shadow,background-color,border-color,color] duration-200 focus-visible:ring-3 focus-visible:ring-ring/15 not-data-[motion=static]:not-data-[variant=floating]:active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-[linear-gradient(180deg,rgba(255,255,255,0.15)_0%,rgba(255,255,255,0)_100%)] bg-primary text-primary-foreground [box-shadow:var(--scaena-shadow-primary)] not-data-[motion=static]:hover:-translate-y-px hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.25)_0%,rgba(255,255,255,0)_100%)] hover:bg-primary-hover hover:[box-shadow:var(--scaena-shadow-primary-hover)] active:[box-shadow:var(--scaena-shadow-primary-active)]",
        outline:
          "border-[var(--scaena-control-border)] bg-white/45 text-foreground [box-shadow:var(--scaena-shadow-control)] backdrop-blur-none md:backdrop-blur-xl not-data-[motion=static]:hover:-translate-y-px hover:border-primary/20 hover:bg-white/75 hover:text-primary aria-expanded:bg-white/80 aria-expanded:text-primary",
        secondary:
          "border-[var(--scaena-control-border)] bg-white text-secondary-foreground [box-shadow:var(--scaena-shadow-solid)] not-data-[motion=static]:hover:-translate-y-px hover:bg-white hover:text-primary-hover aria-expanded:bg-white aria-expanded:text-primary",
        floating:
          "border-white/90 bg-white/72 text-primary [box-shadow:0_18px_42px_-18px_rgba(17,24,39,0.52),0_0_0_1px_rgba(56,49,129,0.08),0_0_28px_-8px_rgba(56,49,129,0.48),inset_0_1px_0_rgba(255,255,255,0.95)] backdrop-blur-none md:backdrop-blur-[56px] hover:bg-white/84 hover:text-primary-hover hover:[box-shadow:0_20px_46px_-18px_rgba(17,24,39,0.58),0_0_0_1px_rgba(56,49,129,0.12),0_0_34px_-8px_rgba(56,49,129,0.58),inset_0_1px_0_rgba(255,255,255,0.98)] aria-expanded:bg-white/86 aria-expanded:text-primary",
        ghost:
          "text-foreground/70 hover:bg-primary/8 hover:text-primary aria-expanded:bg-primary/10 aria-expanded:text-primary",
        navigation:
          "text-muted-foreground hover:bg-primary/8 hover:text-primary aria-[current=page]:bg-primary/8 aria-[current=page]:text-primary",
        inverse:
          "text-white/86 hover:bg-white/10 hover:text-white focus-visible:ring-white/20",
        destructive:
          "border-destructive/12 bg-destructive/10 text-destructive not-data-[motion=static]:hover:-translate-y-px hover:bg-destructive/15 focus-visible:ring-destructive/20",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-[42px] gap-2 px-5 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        xs: "h-8 gap-1.5 rounded-[var(--scaena-radius-compact)] px-3 text-xs has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-[34px] gap-1.5 rounded-[var(--scaena-radius-compact)] px-4 text-xs has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        lg: "h-[50px] gap-2 px-6 text-base has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5",
        icon: "size-[42px]",
        "icon-xs": "size-8 rounded-[var(--scaena-radius-compact)] [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-[34px] rounded-[var(--scaena-radius-compact)]",
        "icon-lg": "size-[50px]",
        dock:
          "h-auto min-h-14 flex-col gap-0.5 rounded-[var(--scaena-radius-inner)] px-1 py-1.5 text-[11px] leading-none [&_svg:not([class*='size-'])]:size-[22px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonPrimitive.Props & VariantProps<typeof buttonVariants>
>(({ className, variant = "default", size = "default", ...props }, ref) => (
  <ButtonPrimitive
    ref={ref}
    data-slot="button"
    data-variant={variant}
    className={cn(buttonVariants({ variant, size, className }))}
    {...props}
  />
))

Button.displayName = "Button"

export { Button, buttonVariants }
